import Vue from 'vue'
import Router from 'vue-router'
import VueCookie from 'vue-cookie'
import store from '@/store/index'
import VueCryptojs from 'vue-cryptojs'
import orders from './modules/order'
import ccsheet from './modules/ccsheet'

// Install plugins
Vue.use(VueCryptojs)
Vue.use(Router)

/**
 * Helper to decrypt a string using the same key you used previously.
 * (We need this later for permissions, if you keep that logic.)
 */
const decryptString = (cipherText) => {
  const bytes = Vue.CryptoJS.AES.decrypt(cipherText, '2020ModulesPermissionKey@TimerApp')
  return bytes.toString(Vue.CryptoJS.enc.Utf8)
}

/**
 * Check a permission‐cookie to see if the current user can access a given module.
 * This is used in router.beforeEach to prevent unauthorized access.
 */
const checkListPermission = (moduleName) => {
  const blob = VueCookie.get(process.env.VUE_APP_PERMISSIONS_AND_ROLES)
  if (!blob) return false
  const { permissions, role } = JSON.parse(decryptString(blob))
  // Superusers always allowed:
  if (['admin', 'superadmin'].includes(role?.slug)) return true
  return (permissions[moduleName] && permissions[moduleName].list) || false
}

// Build a flat array of routes. All should be registered up front.
const routes = [
  // 1) Catch‐all redirect (last fallback)
  { path: '*', redirect: '/dashboard' },

  // 2) Authentication pages
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/Auth/Login.vue'),
    beforeEnter (to, from, next) {
      if (!VueCookie.get(process.env.VUE_APP_TOKEN)) {
        next()
      } else {
        next('/dashboard')
      }
    }
  },
  {
    path: '/directlogin/:otp/:domain/:lang/:user_id',
    name: 'directlogin',
    component: () => import('@/views/Auth/DirectLogin.vue')
  },

  // 3) Dashboard (protected)
  {
    path: '/dashboard',
    component: () => import('@/views/Dashboard/Index.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'dashboard',
        component: () => import('@/views/Dashboard/List.vue')
      }
    ]
  },

  // 4) Other top‐level routes (protected)
  {
    path: '/myshifts',
    name: 'Shifts',
    meta: { requiresAuth: true },
    component: () => import('@/views/MyShifts/MyShift.vue')
  },
  {
    path: '/irscanner',
    component: () => import('@/views/Irscanner/Index.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'irscanner',
        component: () => import('@/views/Irscanner/irscanner.vue')
      },
      {
        path: ':id',
        name: 'irscanner_edit',
        meta: { moduleName: 'irscanner' },
        component: () => import('@/views/Irscanner/Actions.vue')
      }
    ]
  },
  {
    path: '/news',
    name: 'News',
    meta: { requiresAuth: true },
    component: () => import('@/views/News/Index.vue')
  },
  {
    path: '/not_found',
    name: 'NotFound',
    component: () => import('@/components/NotFound.vue')
  },

  // 5) /projects and all nested children
  {
    path: '/projects',
    component: () => import('@/views/Projects/Index.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'projects',
        meta: { moduleName: 'projects' },
        component: () => import('@/views/Projects/List.vue')
      },
      {
        path: ':id',
        name: 'project_action',
        meta: { moduleName: 'projects' },
        component: () => import('@/views/Projects/Actions.vue')
      },
      {
        path: ':project_id/custom_modules/:module_id',
        component: () => import('@/views/Projects/CustomModules/Index.vue'),
        children: [
          {
            path: '',
            name: 'project_custom_modules',
            meta: { moduleName: 'custommodule' },
            component: () => import('@/views/Projects/CustomModules/NewList.vue'),
            props: true
          },
          {
            path: 'action/:record_id?',
            name: 'project_custom_module_action',
            meta: { moduleName: 'custommodule' },
            component: () => import('@/views/Projects/CustomModules/Actions.vue')
          }
        ]
      },
      {
        path: ':project_id/module_report',
        name: 'project_module_report',
        component: () => import('@/views/ModuleReport/Index.vue')
      },
      {
        path: ':project_id/drawing',
        component: () => import('@/views/Projects/Drawings/Index.vue'),
        meta: { requiresAuth: true, moduleName: 'projectdrawing' },
        children: [
          {
            path: '',
            name: 'drawing',
            meta: { moduleName: 'projectdrawing' },
            component: () => import('@/views/Projects/Drawings/NewList.vue')
          },
          {
            path: 'action/:id?',
            name: 'drawing_actions',
            meta: { moduleName: 'projectdrawing' },
            component: () => import('@/views/Projects/Drawings/Actions.vue')
          }
        ]
      },
      {
        path: ':project_id/ndt',
        component: () => import('@/views/Projects/NDT/Index.vue'),
        meta: { requiresAuth: true, moduleName: 'projectndt' },
        children: [
          {
            path: '',
            name: 'ndt',
            meta: { moduleName: 'projectndt' },
            component: () => import('@/views/Projects/NDT/NewList.vue')
          },
          {
            path: 'action/:id?',
            name: 'ndt_actions',
            meta: { moduleName: 'projectndt' },
            component: () => import('@/views/Projects/NDT/Actions.vue')
          }
        ]
      },
      {
        path: ':project_id/pressure-test-report',
        component: () => import('@/views/Projects/PressureTestReport/Index.vue'),
        meta: { requiresAuth: true, moduleName: 'projectpressuretest' },
        children: [
          {
            path: '',
            name: 'pressure-test-report',
            meta: { moduleName: 'projectpressuretest' },
            component: () => import('@/views/Projects/PressureTestReport/NewList.vue')
          },
          {
            path: 'action/:id?',
            name: 'pressure-test-report_actions',
            meta: { moduleName: 'projectpressuretest' },
            component: () => import('@/views/Projects/PressureTestReport/Actions.vue')
          }
        ]
      },
      {
        path: ':project_id/weld-log',
        component: () => import('@/views/Projects/WeldLog/Index.vue'),
        meta: { requiresAuth: true, moduleName: 'projectweldlog' },
        children: [
          {
            path: '',
            name: 'weld-log',
            meta: { moduleName: 'projectweldlog' },
            component: () => import('@/views/Projects/WeldLog/NewList.vue')
          },
          {
            path: 'action/:id?',
            name: 'weld-log_actions',
            meta: { moduleName: 'projectweldlog' },
            component: () => import('@/views/Projects/WeldLog/Actions.vue')
          }
        ]
      },
      {
        path: ':project_id/advanced-weld-log',
        component: () => import('@/views/Projects/AdvancedWeldLog/Index.vue'),
        meta: { requiresAuth: true, moduleName: 'advancedweldlog' },
        children: [{
          path: '/',
          name: 'advanced-weld-log',
          meta: { moduleName: 'advancedweldlog' },
          component: () => import('@/views/Projects/AdvancedWeldLog/ProjectWeldLog.vue')
        }]
      },
      {
        path: ':project_id/surface-report',
        component: () => import('@/views/Projects/SurfaceReport/Index.vue'),
        meta: { requiresAuth: true, moduleName: 'projectsurfacereport' },
        children: [
          {
            path: '',
            name: 'surface-report',
            meta: { moduleName: 'projectsurfacereport' },
            component: () => import('@/views/Projects/SurfaceReport/NewList.vue')
          },
          {
            path: 'action/:id?',
            name: 'surface-report_actions',
            meta: { moduleName: 'projectsurfacereport' },
            component: () => import('@/views/Projects/SurfaceReport/Actions.vue')
          }
        ]
      },
      {
        path: ':project_id/technical-report',
        component: () => import('@/views/Projects/TechnicalReport/Index.vue'),
        meta: { requiresAuth: true, moduleName: 'projecttechnicalreport' },
        children: [
          {
            path: '',
            name: 'technical-report',
            meta: { moduleName: 'projecttechnicalreport' },
            component: () => import('@/views/Projects/TechnicalReport/NewList.vue')
          },
          {
            path: 'action/:id?',
            name: 'technical-report_actions',
            meta: { moduleName: 'projecttechnicalreport' },
            component: () => import('@/views/Projects/TechnicalReport/Actions.vue')
          }
        ]
      },
      {
        path: ':project_id/material-list',
        component: () => import('@/views/Projects/MatrialList/Index.vue'),
        meta: { requiresAuth: true, moduleName: 'projectmateriallist' },
        children: [
          {
            path: '',
            name: 'material-list',
            meta: { moduleName: 'projectmateriallist' },
            component: () => import('@/views/Projects/MatrialList/NewList.vue')
          },
          {
            path: 'action/:id?',
            name: 'material-list_actions',
            meta: { moduleName: 'projectmateriallist' },
            component: () => import('@/views/Projects/MatrialList/Actions.vue')
          }
        ]
      },
      {
        path: ':project_id/pwht',
        component: () => import('@/views/Projects/PWHT/Index.vue'),
        meta: { requiresAuth: true, moduleName: 'projectpwht' },
        children: [
          {
            path: '',
            name: 'pwht',
            meta: { moduleName: 'projectpwht' },
            component: () => import('@/views/Projects/PWHT/NewList.vue')
          },
          {
            path: 'action/:id?',
            name: 'pwht_actions',
            meta: { moduleName: 'projectpwht' },
            component: () => import('@/views/Projects/PWHT/Actions.vue')
          }
        ]
      },
      {
        path: ':project_id/pwps',
        component: () => import('@/views/Projects/PWPS/Index.vue'),
        meta: { requiresAuth: true, moduleName: 'projectpwps' },
        children: [
          {
            path: '',
            name: 'pwps',
            meta: { moduleName: 'projectpwps' },
            component: () => import('@/views/Projects/PWPS/NewList.vue')
          },
          {
            path: 'action/:id?',
            name: 'pwps_actions',
            meta: { moduleName: 'projectpwps' },
            component: () => import('@/views/Projects/PWPS/Actions.vue')
          }
        ]
      },
      {
        path: ':project_id/wps',
        component: () => import('@/views/Projects/WPS/Index.vue'),
        meta: { requiresAuth: true, moduleName: 'projectwps' },
        children: [
          {
            path: '',
            name: 'wps',
            meta: { moduleName: 'projectwps' },
            component: () => import('@/views/Projects/WPS/NewList.vue')
          },
          {
            path: 'action/:id?',
            name: 'wps_actions',
            meta: { moduleName: 'projectwps' },
            component: () => import('@/views/Projects/WPS/Actions.vue')
          }
        ]
      }
    ]
  },

  // 6) /projects/:project_id/materials
  {
    path: '/projects/:project_id/materials',
    component: () => import('@/views/Warehouse/orders/customer/View'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        component: () => import('@/views/Warehouse/orders/customer/Item')
      },
      {
        path: 'pick/:product_code?',
        name: 'project_pick_material',
        component: () => import('@/views/Warehouse/orders/customer/Pick')
      }
    ]
  },

  // 7) /projects/:project_id/checklists
  {
    path: '/projects/:project_id/checklists',
    name: 'project_checklist',
    component: () => import('@/views/Projects/Checklist/List.vue')
  },

  // 8) /projects/:project_id/equipments
  {
    path: '/projects/:project_id/equipments',
    name: 'project_equipment',
    meta: { moduleName: 'projectequipment' },
    component: () => import('@/views/Equipments/NewListEquipement.vue')
  },

  // 9) /projects/:project_id/deviations
  {
    path: '/projects/:project_id/deviations',
    name: 'project_deviation',
    meta: { moduleName: 'projectdeviation' },
    component: () => import('@/views/Deviations/NewList.vue')
  },

  // 10) /files
  {
    path: '/files',
    component: () => import('@/views/Files/Index.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'files',
        component: () => import('@/views/Files/Files.vue')
      }
    ]
  },

  // 11) /deviations
  {
    path: '/deviations',
    component: () => import('@/views/Deviations/Index.vue'),
    meta: { requiresAuth: true, moduleName: 'deviation' },
    children: [
      {
        path: '',
        name: 'deviations',
        meta: { moduleName: 'deviation' },
        component: () => import('@/views/Deviations/NewList.vue')
      },
      {
        path: 'add',
        name: 'deviation_add',
        meta: { moduleName: 'deviation' },
        component: () => import('@/views/Deviations/Actions.vue')
      },
      {
        path: ':id',
        name: 'deviation_edit',
        meta: { moduleName: 'deviation' },
        component: () => import('@/views/Deviations/Actions.vue')
      }
    ]
  },

  // 12) /calendar
  {
    path: '/calendar',
    component: () => import('@/views/Calendar/Index.vue'),
    meta: { requiresAuth: true },
    children: [
      {
        path: '',
        name: 'calendar',
        component: () => import('@/views/Calendar/Calendar.vue')
      },
      {
        path: 'actions/:hour_id?',
        name: 'calendar_actions',
        component: () => import('@/views/Calendar/Action.vue')
      }
    ]
  },

  // 13) /stemp
  {
    path: '/stemp',
    name: 'stemp',
    meta: { requiresAuth: true },
    component: require('@/views/Timer/Stemp.vue').default
  },

  // 14) /global_checklist
  {
    path: '/global_checklist',
    component: () => import('@/views/GlobalChecklist/Index.vue'),
    meta: { requiresAuth: true, moduleName: 'checklists' },
    children: [
      {
        path: '',
        name: 'global_checklist',
        meta: { moduleName: 'checklists' },
        component: () => import('@/views/GlobalChecklist/List.vue')
      }
    ]
  },

  // 15) /chemicallist
  {
    path: '/chemicallist',
    component: () => import('@/views/Chemicallist/Index.vue'),
    meta: { requiresAuth: true, moduleName: 'chemicallist' },
    children: [
      {
        path: '',
        name: 'chemicallist',
        meta: { moduleName: 'chemicallist' },
        component: () => import('@/views/Chemicallist/NewList.vue')
      },
      {
        path: ':chemicallist_id/checklists',
        name: 'chemicallist_checklist',
        meta: { moduleName: 'chemicallist' },
        component: () => import('@/views/Chemicallist/Checklist/List.vue')
      },
      {
        path: ':id',
        name: 'chemicallist_edit',
        meta: { moduleName: 'chemicallist' },
        component: () => import('@/views/Chemicallist/Actions.vue')
      }
    ]
  },

  // 16) /equipments
  {
    path: '/equipments',
    component: () => import('@/views/Equipments/Index.vue'),
    meta: { requiresAuth: true, moduleName: 'equipment' },
    children: [
      {
        path: '',
        name: 'equipments',
        meta: { moduleName: 'equipment' },
        component: () => import('@/views/Equipments/NewListEquipement.vue')
      },
      {
        path: 'add',
        name: 'equipment_add',
        meta: { moduleName: 'equipment' },
        component: () => import('@/views/Equipments/Actions.vue')
      },
      {
        path: ':id',
        name: 'equipment_edit',
        meta: { moduleName: 'equipment' },
        component: () => import('@/views/Equipments/Actions.vue')
      }
    ]
  },

  // 17) /equipments/:equipment_id/checklists
  {
    path: '/equipments/:equipment_id/checklists',
    name: 'equipment_checklist',
    component: () => import('@/views/Equipments/Checklist/List.vue')
  },

  // 18) /warehouse
  {
    path: '/warehouse',
    component: () => import('@/views/Warehouse/dashboard/Index'),
    meta: { requiresAuth: true }
  },
  // 19) Spread in routes from orders and ccsheet modules
  ...orders,
  ...ccsheet,

  // 20) /tasks
  {
    path: '/tasks',
    meta: { requiresAuth: true },
    component: () => import('@/views/Tasks/Index.vue'),
    children: [
      {
        path: '',
        name: 'Tasks',
        component: () => import('@/views/Tasks/List.vue')
      }
    ]
  },
  // 21) /checklist_files
  {
    path: '/checklist_files',
    component: () => import('@/views/ChecklistFiles/ChecklistFiles.vue'),
    meta: { requiresAuth: true }
  }
]

const router = new Router({
  mode: 'history',
  routes
})

// Modules that require a permission check
const permissionModules = [
  'custommodule',
  'equipment',
  'chemicallist',
  'deviation',
  'checklists',
  'projects',
  'projectdeviation',
  'projectequipment',
  'projectdrawing',
  'projectndt',
  'projectpressuretest',
  'projectweldlog',
  'projectsurfacereport',
  'projecttechnicalreport',
  'projectmateriallist',
  'projectpwht',
  'projectpwps',
  'projectwps'
]

// Global navigation guard
router.beforeEach((to, from, next) => {
  if (to.matched.some(record => record.meta.requiresAuth)) {
    // If no token → redirect to /login
    if (!VueCookie.get(process.env.VUE_APP_TOKEN)) {
      store.commit('resetAuth', false)
      store.commit('resetPermissions', false)
      VueCookie.delete(process.env.VUE_APP_TOKEN)
      next({
        path: '/login',
        query: {
          redirect: to.fullPath
        }
      })
      return
    }

    // If route has moduleName → check cookie permission
    const activeRoute = to.matched.find(record => record.name === to.name)
    if (
      activeRoute &&
      activeRoute.meta &&
      activeRoute.meta.moduleName &&
      permissionModules.includes(activeRoute.meta.moduleName)
    ) {
      if (checkListPermission(activeRoute.meta.moduleName)) {
        next()
      } else {
        next({ path: '/' })
      }
    } else {
      next()
    }
  } else {
    next()
  }
})

export default router
